import React, { useState, useCallback } from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@material-ui/core";
import { useSelector } from "react-redux";
import useCurrentFacility from "../../hooks/useCurrentFacility";
import { useEnqueueSnackbar } from "../../hooks/useEnqueueSnackbar";
import RewardGroupService from "../../services/RewardGroupService";
import apiClient from "../../auth/apiClient";
import useRewardGroups from "./useRewardGroups";
import RewardGroupsList from "./RewardGroupsList";
import RewardGroupDrawer from "./RewardGroupDrawer";
import { useStyles } from "./styles";
import clsx from "clsx";

const service = new RewardGroupService(apiClient);

const RewardGroupsContainer = () => {
  const classes = useStyles();
  const { facilityID } = useCurrentFacility();
  const enqueueSnackbar = useEnqueueSnackbar();

  const scopeAwareFacilityID = useSelector(
    (state) => state.entityScope?.facilityGroupId || facilityID
  );

  const {
    data,
    loading,
    error,
    pagination,
    setPagination,
    refetch,
  } = useRewardGroups(scopeAwareFacilityID);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("add");
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [accessGroups, setAccessGroups] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchAccessGroups = useCallback(async () => {
    try {
      const response = await service.getEligibleAccessGroups(scopeAwareFacilityID);
      setAccessGroups(response.data || []);
    } catch {
      enqueueSnackbar("Failed to load access groups.", {
        variant: "error",
        tag: "RewardGroupsAccessGroupsError",
      });
    }
  }, [scopeAwareFacilityID, enqueueSnackbar]);

  const handleAdd = async () => {
    await fetchAccessGroups();
    setDrawerMode("add");
    setSelectedGroup(null);
    setDrawerOpen(true);
  };

  const handleEdit = async (row) => {
    try {
      await fetchAccessGroups();
      const response = await service.getRewardGroup(
        scopeAwareFacilityID,
        row.rewardGroupID
      );
      setSelectedGroup(response.data);
      setDrawerMode("edit");
      setDrawerOpen(true);
    } catch {
      enqueueSnackbar("Failed to load reward group details.", {
        variant: "error",
        tag: "RewardGroupsFetchError",
      });
    }
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedGroup(null);
  };

  const handleSave = async (payload) => {
    setIsSaving(true);
    try {
      if (drawerMode === "add") {
        await service.createRewardGroup(scopeAwareFacilityID, payload);
      } else {
        await service.updateRewardGroup(
          scopeAwareFacilityID,
          selectedGroup.rewardGroupID,
          payload
        );
      }
      handleDrawerClose();
      refetch();
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data ||
        "Failed to save reward group.";
      enqueueSnackbar(String(message), {
        variant: "error",
        tag: "RewardGroupsSaveError",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleListDelete = (row) => {
    setDeleteTarget(row);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await service.deleteRewardGroup(
        scopeAwareFacilityID,
        deleteTarget.rewardGroupID
      );
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      refetch();
    } catch {
      enqueueSnackbar("Failed to delete reward group.", {
        variant: "error",
        tag: "RewardGroupsDeleteError",
      });
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  return (
    <Box p={2}>
      <Paper className={classes.containerPaper} elevation={0}>
        <Grid container className={classes.cardHeader}>
          <Grid item xs={8} md={8} lg={8} className={clsx("onepark-reward-groups-container", classes.headerTitle)}>
            <Typography className={classes.cardHeaderText}>
              LOYALTY REWARDS
            </Typography>
          </Grid>
          <Grid item xs={4} md={4} lg={4} className={classes.headerAddButton}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAdd}
              className={clsx("onepark-reward-groups-container", classes.addButton)}
            >
              ADD
            </Button>
          </Grid>
        </Grid>

        <RewardGroupsList
          data={data}
          loading={loading}
          error={error}
          pagination={pagination}
          onPaginationChange={setPagination}
          onEdit={handleEdit}
          onDelete={handleListDelete}
          onRetry={refetch}
        />
      </Paper>

      <RewardGroupDrawer
        open={drawerOpen}
        onClose={handleDrawerClose}
        mode={drawerMode}
        group={selectedGroup}
        accessGroups={accessGroups}
        onSave={handleSave}
        isSaving={isSaving}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Delete Rewards Group</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this group?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            onClick={handleDeleteConfirm}
          >
            Yes
          </Button>
          <Button
            variant="contained"
            onClick={handleDeleteCancel}
            className={classes.deleteDialogNoBtn}
          >
            No
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RewardGroupsContainer;
